import { useState } from "react";
import { IconBooks } from "@tabler/icons-react";
import { Table } from "../UI/Table";
import Status from "../UI/Status";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import EmptyState from "../UI/EmptyState";
import Text from "../UI/Text";
import Title from "../UI/Title";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import { dateConvert } from "../../api/Functions";
import MoisPicker from "./MoisPicker";
import { useGrandLivre } from "../../hooks/use-compta-pro";
import type {
  GrandLivreSection,
  TypeCompteComptable,
} from "../../models/comptabilite";
import {
  euros,
  periodeCourante,
  TYPE_COMPTE_LABELS,
  type Periode,
} from "./comptaUtils";
import styles from "./Compta.module.css";

interface TypeOption {
  value: TypeCompteComptable | "";
  label: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  { value: "", label: "Tous les comptes" },
  ...(
    Object.entries(TYPE_COMPTE_LABELS) as [TypeCompteComptable, string][]
  ).map(([value, label]) => ({ value, label })),
];

// Comptes affichés en solde débiteur (miroir de DEBIT_NATURE côté back)
const TYPES_DEBITEURS: TypeCompteComptable[] = [
  "banque",
  "locataire",
  "attente",
  "virement_interne",
  "prelevement_honoraires",
];

function CompteSection({ section }: { section: GrandLivreSection }) {
  const { compte, entries, totaux } = section;
  const soldeDebiteur = TYPES_DEBITEURS.includes(compte.type);
  let soldeProgressif = 0;

  return (
    <div className={styles.compteSection}>
      <div className={styles.compteHeader}>
        <Title variant="hecto">
          {compte.numero} — {compte.label}
        </Title>
        <Status
          color="neutral"
          text={TYPE_COMPTE_LABELS[compte.type] ?? compte.type}
        />
        <div className={styles.toolbarSpacer}>
          <Text
            as="span"
            weight="bold"
            color={parseFloat(totaux.solde) < 0 ? "danger" : undefined}
          >
            Solde : {euros(totaux.solde)}
          </Text>
        </div>
      </div>
      <Table
        variant="card"
        expandable
        rowCount={entries.length + 1}
        collapsedRows={6}
      >
        <Table.Header>
          <Table.Column>Date</Table.Column>
          <Table.Column>Journal</Table.Column>
          <Table.Column>Pièce</Table.Column>
          <Table.Column>Libellé</Table.Column>
          <Table.Column align="right">Débit</Table.Column>
          <Table.Column align="right">Crédit</Table.Column>
          <Table.Column align="right">Solde</Table.Column>
        </Table.Header>
        <Table.Body>
          {entries.map((entry) => {
            const debit = entry.lines
              .filter((line) => line.numero === compte.numero)
              .reduce((sum, line) => sum + parseFloat(line.debit), 0);
            const credit = entry.lines
              .filter((line) => line.numero === compte.numero)
              .reduce((sum, line) => sum + parseFloat(line.credit), 0);
            soldeProgressif += soldeDebiteur ? debit - credit : credit - debit;
            return (
              <Table.Row key={entry.piece_ref}>
                <Table.Cell>{dateConvert(entry.ecriture_date)}</Table.Cell>
                <Table.Cell>
                  <Status color="neutral" text={entry.journal} />
                </Table.Cell>
                <Table.Cell>{entry.piece_ref}</Table.Cell>
                <Table.Cell>{entry.label}</Table.Cell>
                <Table.Cell align="right">
                  {debit !== 0 ? euros(debit) : ""}
                </Table.Cell>
                <Table.Cell align="right">
                  {credit !== 0 ? euros(credit) : ""}
                </Table.Cell>
                <Table.Cell align="right">
                  <Text
                    as="span"
                    color={soldeProgressif < 0 ? "danger" : undefined}
                  >
                    {euros(soldeProgressif)}
                  </Text>
                </Table.Cell>
              </Table.Row>
            );
          })}
          <Table.Row>
            <Table.Cell colSpan={4}>
              <Text as="span" weight="bold">
                Total
              </Text>
            </Table.Cell>
            <Table.Cell align="right">
              <Text as="span" weight="bold">
                {euros(totaux.total_debit)}
              </Text>
            </Table.Cell>
            <Table.Cell align="right">
              <Text as="span" weight="bold">
                {euros(totaux.total_credit)}
              </Text>
            </Table.Cell>
            <Table.Cell align="right">
              <Text as="span" weight="bold">
                {euros(totaux.solde)}
              </Text>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </div>
  );
}

export default function GrandLivreTab() {
  const [periode, setPeriode] = useState<Periode>(periodeCourante());
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeOption>(TYPE_OPTIONS[0]);
  const { data, isLoading } = useGrandLivre(periode.year, periode.month);

  const searchLower = search.toLowerCase();
  const comptes = (data?.comptes ?? []).filter((section) => {
    if (typeFilter.value && section.compte.type !== typeFilter.value) {
      return false;
    }
    return (
      !searchLower ||
      section.compte.numero.includes(searchLower) ||
      section.compte.label.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className={styles.stack}>
      <div className={styles.toolbar}>
        <TextInputStandalone
          label="Rechercher"
          placeholder="Numéro ou libellé de compte"
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
        />
        <ReactSelectStandalone
          label="Type de compte"
          className={styles.journalSelect}
          options={TYPE_OPTIONS}
          value={typeFilter}
          onChange={(option: TypeOption) => setTypeFilter(option)}
        />
        <MoisPicker
          label="Situation à fin"
          className={styles.moisPicker}
          value={periode}
          onChange={setPeriode}
        />
        <div className={styles.toolbarSpacer}>
          {data && <Text>{comptes.length} compte(s) mouvementé(s)</Text>}
        </div>
      </div>
      {data && data.anomalies.length > 0 && (
        <Callout variant="warning" title="Anomalies de projection">
          {data.anomalies.join(" — ")}
        </Callout>
      )}
      {isLoading ? (
        <IsLoading />
      ) : comptes.length === 0 ? (
        <EmptyState
          icon={IconBooks}
          title="Aucun compte mouvementé"
          description="Le grand livre liste tous les comptes du plan avec leurs écritures, depuis le début du premier mandat jusqu'au mois choisi."
        />
      ) : (
        comptes.map((section) => (
          <CompteSection key={section.compte.numero} section={section} />
        ))
      )}
    </div>
  );
}
